<template>
  <div class="py-12">
    <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div class="overflow-hidden bg-white shadow sm:rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg font-medium leading-6 text-gray-900">System Constants</h3>
          <p class="mt-1 text-sm text-gray-500">
            Manage system-wide constants that affect fees and charges.
          </p>

          <div v-if="loading" class="flex justify-center items-center py-8">
            <div class="w-8 h-8 rounded-full border-4 border-blue-500 animate-spin border-t-transparent"></div>
          </div>

          <div v-else-if="error" class="p-4 mt-4 text-red-700 bg-red-100 rounded-lg">
            {{ error }}
          </div>

          <form v-else @submit.prevent="updateConstants" class="mt-6 space-y-6">
            <!-- Customer Service Fee Percentage -->
            <div>
              <label for="customer_service_fee_percentage" class="block text-sm font-medium text-gray-700">
                Customer Service Fee Percentage
              </label>
              <div class="mt-1">
                <div class="relative rounded-md shadow-sm">
                  <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">%</span>
                  </div>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    max="1"
                    id="customer_service_fee_percentage"
                    v-model="constants.customer_service_fee_percentage"
                    class="block p-2 pl-8 w-full rounded-md border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
              </div>
              <p class="mt-2 text-sm text-gray-500">
                {{ descriptions.customer_service_fee_percentage }} (currently {{ (constants.customer_service_fee_percentage * 100).toFixed(1) }}%)
              </p>
            </div>

            <!-- Max Customer Service Fee -->
            <div>
              <label for="max_customer_service_fee" class="block text-sm font-medium text-gray-700">
                Max Customer Service Fee
              </label>
              <div class="mt-1">
                <div class="relative rounded-md shadow-sm">
                  <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">{{ nairaSymbol }}</span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    id="max_customer_service_fee"
                    v-model="constants.max_customer_service_fee"
                    class="block p-2 pl-8 w-full rounded-md border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
              </div>
              <p class="mt-2 text-sm text-gray-500">
                {{ descriptions.max_customer_service_fee }}
              </p>
            </div>

            <!-- Customer Delivery Fee -->
            <div>
              <label for="customer_delivery_fee" class="block text-sm font-medium text-gray-700">
                Customer Delivery Fee
              </label>
              <div class="mt-1">
                <div class="relative rounded-md shadow-sm">
                  <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">{{ nairaSymbol }}</span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    id="customer_delivery_fee"
                    v-model="constants.customer_delivery_fee"
                    class="block p-2 pl-8 w-full rounded-md border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
              </div>
              <p class="mt-2 text-sm text-gray-500">
                {{ descriptions.customer_delivery_fee }}
              </p>
            </div>

            <!-- Business Service Fee Percentage -->
            <div>
              <label for="business_service_fee_percentage" class="block text-sm font-medium text-gray-700">
                Business Service Fee Percentage
              </label>
              <div class="mt-1">
                <div class="relative rounded-md shadow-sm">
                  <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">%</span>
                  </div>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    max="1"
                    id="business_service_fee_percentage"
                    v-model="constants.business_service_fee_percentage"
                    class="block p-2 pl-8 w-full rounded-md border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
              </div>
              <p class="mt-2 text-sm text-gray-500">
                {{ descriptions.business_service_fee_percentage }} (currently {{ (constants.business_service_fee_percentage * 100).toFixed(1) }}%)
              </p>
            </div>

            <!-- Max Business Service Fee -->
            <div>
              <label for="max_business_service_fee" class="block text-sm font-medium text-gray-700">
                Max Business Service Fee
              </label>
              <div class="mt-1">
                <div class="relative rounded-md shadow-sm">
                  <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">{{ nairaSymbol }}</span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    id="max_business_service_fee"
                    v-model="constants.max_business_service_fee"
                    class="block p-2 pl-8 w-full rounded-md border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
              </div>
              <p class="mt-2 text-sm text-gray-500">
                {{ descriptions.max_business_service_fee }}
              </p>
            </div>

            <div class="flex justify-end">
              <button
                type="submit"
                :disabled="saving"
                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md border border-transparent shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                {{ saving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import api from '@/services/axios'
import { nairaSymbol } from '@/constants'

const toast = useToast()
const loading = ref(true)
const saving = ref(false)
const error = ref(null)

const constants = ref({
  customer_service_fee_percentage: 0.075,
  customer_delivery_fee: 2000,
  business_service_fee_percentage: 0.05,
  max_customer_service_fee: 1500,
  max_business_service_fee: 1500,
})

const descriptions = ref({
  customer_service_fee_percentage: 'The percentage of the order total that goes to the platform as service fee',
  customer_delivery_fee: 'The fixed delivery fee charged to customers for each order',
  business_service_fee_percentage: 'The percentage of the order total that the platform takes from businesses',
  max_customer_service_fee: 'The maximum amount that can be charged as customer service fee (in kobo)',
  max_business_service_fee: 'The maximum amount that can be charged as business service fee (in kobo)',
})

const fetchConstants = async () => {
  try {
    loading.value = true
    const response = await api.get('/api/constants')
    constants.value = response.data
  } catch (err) {
    error.value = err.message
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch constants',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

const updateConstants = async () => {
  try {
    saving.value = true
    await api.post('/api/admin/constants', constants.value)
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Constants updated successfully',
      life: 3000,
    })
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err.response?.data?.message || 'Failed to update constants',
      life: 3000,
    })
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  fetchConstants()
})
</script>

<style lang="scss" scoped></style>
